import { BrowserSessionApiClient } from './browserSessionApiClient';

export type ProjectMemoryJobMode = 'refresh' | 'backfill';
export type ProjectMemoryJob = {
  jobId: string;
  projectId: number;
  mode: ProjectMemoryJobMode;
  status: 'queued' | 'running' | 'failed' | 'completed';
  progress: { recordsRead: number; pagesRead: number; domainsCompleted: number; domainsTotal: number; currentDomain: string | null };
  updatedAt: { value: string; label: string };
  errorCode: string | null;
  message: string;
  coverage: 'pending' | 'complete' | 'partial';
  sources?: Array<{ domain: string; status: 'pending' | 'current' | 'restricted' | 'unavailable'; complete: boolean; pages: number; records: number }>;
};

const client = new BrowserSessionApiClient((import.meta.env.VITE_API_BASE_URL || '/api').replace(/\/+$/, ''));
const invalid = () => new Error('Project memory status could not be verified. Check status again.');
const text = (value: unknown, max: number): value is string => typeof value === 'string' && value.length <= max;
function basePath(projectId: number): string {
  if (!Number.isSafeInteger(projectId) || projectId <= 0 || projectId > 2147483647) throw invalid();
  return `/api/v1/projects/${projectId}/memory/jobs`;
}
function jobPath(projectId: number, jobId: string): string {
  if (!/^[a-zA-Z0-9-]{1,100}$/.test(jobId)) throw invalid();
  return `${basePath(projectId)}/${encodeURIComponent(jobId)}`;
}
function readJob(response: unknown, projectId: number, allowNull = false): ProjectMemoryJob | null {
  const envelope = response as { data?: unknown } | null;
  const data = (envelope?.data ?? response) as { job?: unknown } | null;
  if (allowNull && data?.job === null) return null;
  const job = data?.job as ProjectMemoryJob | null | undefined;
  if (!job || !text(job.jobId, 100) || !/^[a-zA-Z0-9-]+$/.test(job.jobId) || job.projectId !== projectId
    || !['refresh', 'backfill'].includes(job.mode) || !['queued', 'running', 'failed', 'completed'].includes(job.status)
    || !['pending', 'complete', 'partial'].includes(job.coverage) || !text(job.message, 2000)
    || (job.errorCode !== null && !text(job.errorCode, 100)) || !job.updatedAt || !text(job.updatedAt.value, 200) || !text(job.updatedAt.label, 100)
    || !job.progress || !['recordsRead', 'pagesRead', 'domainsCompleted', 'domainsTotal'].every(key => {
      const value = job.progress[key as keyof ProjectMemoryJob['progress']];
      return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
    }) || (job.progress.currentDomain !== null && !text(job.progress.currentDomain, 160))
    || (job.sources !== undefined && (!Array.isArray(job.sources) || job.sources.length > 20 || job.sources.some(source => !source
      || !text(source.domain, 160) || !['pending', 'current', 'restricted', 'unavailable'].includes(source.status)
      || typeof source.complete !== 'boolean' || !Number.isSafeInteger(source.pages) || source.pages < 0
      || !Number.isSafeInteger(source.records) || source.records < 0)))) throw invalid();
  return job;
}
function mutationOptions(idempotencyKey: string) {
  if (!/^[a-zA-Z0-9:_-]{1,160}$/.test(idempotencyKey)) throw invalid();
  return { method: 'POST', headers: { 'Idempotency-Key': idempotencyKey } };
}

export const projectMemoryService = {
  async getLatest(projectId: number): Promise<ProjectMemoryJob | null> {
    return readJob(await client.request(`${basePath(projectId)}/latest`), projectId, true);
  },
  async getJob(projectId: number, jobId: string): Promise<ProjectMemoryJob> {
    const job = readJob(await client.request(jobPath(projectId, jobId)), projectId)!;
    if (job.jobId !== jobId) throw invalid();
    return job;
  },
  async start(projectId: number, mode: ProjectMemoryJobMode, idempotencyKey: string): Promise<ProjectMemoryJob> {
    if (!['refresh', 'backfill'].includes(mode)) throw invalid();
    return readJob(await client.request(basePath(projectId), { ...mutationOptions(idempotencyKey), body: { mode } }), projectId)!;
  },
  async resume(projectId: number, jobId: string, idempotencyKey: string): Promise<ProjectMemoryJob> {
    const job = readJob(await client.request(`${jobPath(projectId, jobId)}/resume`, mutationOptions(idempotencyKey)), projectId)!;
    if (job.jobId !== jobId) throw invalid();
    return job;
  },
};
